import { logError } from '@databases/logs';
import { type DefaultRootState, type DefaultStore } from '@databases/redux';
import { ExportFileType, RenderContext } from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import { TranslatableError } from '@databases/utils/errors';
import { getImageUrl } from '@databases/utils/media-client';
import { selectBaseUrl } from '@databases/utils/redux/context/selectors';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import type { IntlShape } from 'react-intl-next';

import { exportDatabase, exportDatabaseToHtml, exportEntryToHtml } from './export-utils';

function downloadFile(file: File): void {
  const fileURL = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = fileURL;
  link.download = file.name;
  link.click();
}

export async function exportAndDownloadDatabase(
  {
    contentId,
    exportType,
    viewSettings,
    selectDatabase = selectDatabaseByContentIdOptional,
    databaseName,
  }: {
    contentId: string;
    exportType: ExportFileType;
    viewSettings?: DatabaseViewSettings;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
    databaseName: string;
  },
  store: DefaultStore,
  intl: IntlShape
): Promise<void> {
  switch (exportType) {
    case ExportFileType.HTML:
    case ExportFileType.CSV:
      downloadFile(
        await exportDatabase(
          { contentId, exportType, viewSettings, selectDatabase, databaseName },
          store,
          intl
        )
      );
      break;

    case ExportFileType.PDF:
      await openPrintDatabasePopup(
        { contentId, viewSettings, selectDatabase, databaseName },
        store.getState(),
        intl
      );
      break;

    default:
      throw new TranslatableError(
        `Unsupported export type ${exportType}.`,
        'snackbar-error-message.unsupported-export-type',
        { exportType }
      );
  }
}

const printStyle = `
    #loading {
        position: fixed;
        top: calc(50% - 25px);
        left: calc(50% - 25px);
        animation: spin 0.86s cubic-bezier(0.4, 0.15, 0.6, 0.85) infinite;
    }

    @keyframes spin {
        100% {
            transform: rotate(360deg);
        }
    }
`;

async function openPrintDatabasePopup(
  {
    contentId,
    viewSettings,
    selectDatabase = selectDatabaseByContentIdOptional,
    databaseName,
  }: {
    contentId: string;
    viewSettings?: DatabaseViewSettings;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
    databaseName: string;
  },
  state: DefaultRootState,
  intl: IntlShape
): Promise<void> {
  await openPrintPopup(
    async () =>
      await exportDatabaseToHtml(
        {
          contentId,
          renderContext: RenderContext.PRINT_PREVIEW,
          viewSettings,
          selectDatabase,
          databaseName,
        },
        state,
        intl
      ),
    state,
    contentId
  );
}

export async function openPrintEntryPopup(
  {
    contentId,
    entryId,
    selectDatabase = selectDatabaseByContentIdOptional,
  }: {
    contentId: string;
    entryId: string;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  },
  state: DefaultRootState
): Promise<void> {
  await openPrintPopup(
    async () =>
      await exportEntryToHtml(
        {
          contentId,
          entryId,
          renderContext: RenderContext.PRINT_PREVIEW,
          selectDatabase,
        },
        state
      ),
    state,
    contentId
  );
}

async function openPrintPopup(
  getHtml: () => Promise<string>,
  state: DefaultRootState,
  contentId: string
): Promise<void> {
  const popup = open(
    undefined,
    undefined,
    `popup=1,height=${screen.height},width=${Math.min(screen.width, 1100)}`
  );
  if (!popup) {
    throw new TranslatableError(
      'Could not open popup window.',
      'snackbar-error-message.could-not-open-popup-window'
    );
  }

  const styleEl = popup.document.createElement('style');
  styleEl.textContent = printStyle;
  popup.document.head.appendChild(styleEl);

  const spinner = popup.document.createElement('div');
  spinner.innerHTML =
    '<svg id="loading" height="50" viewbox="0 0 50 50" width="50" xmlns="http://www.w3.org/2000/svg"><circle class="spin-circle" cx="25" cy="25" fill="none" r="22.5" stroke-linecap="round" stroke-width="5" style="stroke: #42526e; transform-origin: center; transition: stroke-dashoffset 0.8s ease-in-out, opacity 0.2s ease-in-out; transition-delay: initial; stroke-dashoffset: 113.097px; stroke-dasharray: 141.371px;"></circle></svg>';
  popup.document.body.appendChild(spinner);

  const htmlExport = await getHtml();

  // Load export view into temporary document to prevent images from being loaded (so that we can replace them by data URLs first)
  const tempDocument = new DOMParser().parseFromString(htmlExport, 'text/html');

  const stylesP = Promise.all(
    Array.from(popup.document.importNode(tempDocument.head, true).childNodes).map(
      async (node) => {
        popup.document.head.appendChild(node);
        if (
          ['[object HTMLStyleElement]', '[object HTMLLinkElement]'].includes(`${node}`) &&
          (node as HTMLElement).matches('style,link[rel=stylesheet]')
        ) {
          await new Promise((resolve) => {
            node.addEventListener('load', resolve);
            node.addEventListener('error', resolve);
          });
        }
      }
    )
  );

  const baseUrl = selectBaseUrl(state);

  const images = Array.from(tempDocument.body.querySelectorAll('img'));
  await Promise.all(
    images.map(async (img) => {
      if (img.src.startsWith(baseUrl)) {
        const fileId = img.getAttribute('data-image-id');
        const imageUrl = fileId
          ? (await getImageUrl(state, contentId, fileId)) || ''
          : '';
        if (imageUrl === '') {
          const e = new Error('Could not get image url');
          logError(e.message, e);
        }
        img.src = imageUrl;
      }
    })
  );

  await stylesP;

  spinner.remove();
  for (const node of Array.from(
    popup.document.importNode(tempDocument.body, true).childNodes
  )) {
    popup.document.body.appendChild(node);
  }

  const popupImages = Array.from(popup.document.body.querySelectorAll('img'));
  await Promise.all(
    popupImages.map(async (img) => {
      await new Promise((resolve) => {
        img.addEventListener('load', () => resolve(undefined));
        img.addEventListener('error', () => resolve(undefined));
      });
    })
  );

  popup.print();
}
